
import { Logger } from '@/util/logger';

/**
 *
 * @class Kinetic
 *
 * @brief this animator or used to simulate physical momentum for x/y panning.
 *
 * For kinetic panning, we use the ideas from:
 *
 *   https://ariya.io/tags/kinetic
 *   https://www.youtube.com/watch?v=JU5ImU1QHU4
 *   https://github.com/anvaka/panzoom/
 *
 * This approach lets us create a physical momentum to the panning.  We don't use it for zooming,
 * since the mouse wheel interaction is generally fine-grained, and linear is fine.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      May 2023
 *
 */
export class Kinetic {

  /** the momentum panning callback */
  private autoScrollID: number                          = 0;

  /** the polling callback to track mouse dragging */
  private velocityID: number                            = 0;

  /** the timestamp for last operation (milliseconds) */
  private timestamp: number                             = 0;

  /** the decay duration; 342, gives ~2sec of animation */
  private timeConstant: number                          = 342;

  /** the last position we were at */
  private lastPosition: {x: number, y: number}          = {x: 0, y: 0};

  /** horizontal dragging velocity */
  private vx: number                                    = 0;

  /** vertical dragging velocity */
  private vy: number                                    = 0;

  /** horizontal launch velocity for momentum panning */
  private ax: number                                    = 0;

  /** vertical launch velocity for momentum panning */
  private ay: number                                    = 0;

  /** hte horizontal launch position */
  private tx: number                                    = 0;

  /** the vertical launch position */
  private ty: number                                    = 0;

  /** [INPUT] client function to get the current screen position */
  private getPosition: () => {x: number, y: number}     = () => {return {x: 0, y: 0};};

  /** [OUTPUT] client function to actually do the panning */
  private setPosition: (x: number, y: number) => void   = (x: number, y: number): void => {};

  /** the client function to call when auto-scrolling has finished */
  private completed: () => void                         = (): void => {};

  /** minimum velocity */
  private minVelocity: number                           = 5;

  /** amplitude */
  private amplitude: number                             = 0.25;

  /** helper flag for when we were forced to cancel */
  private canceled: boolean                             = false;

  /**
   * standard constructor, you must provide a callback to be used to fetch the current screen
   * position.
   *
   * @param getPosition you must provide a call back that fetches the current position of
   * the thing being panned.
   *
   * @param setPosition you must provide a call back that sets the position of the thing
   * being panned.
   *
   * @param completed you must provide a call back to notify you when auto-scrolling
   * has completed.
   *
   * @param minimumVelocity this is the threshold for enabling momentum scrolling; if you
   * drag slowly we don't do the auto-scrolling.
   *
   * @param amplitude scale factor for how big to make the overall effect
   *
   * @param timeConstant this is the time scaling factor; 342 gives you ~2sec of animation
   *
   */
  constructor(getPosition: () => {x: number, y: number},
              setPosition: (x: number, y: number) => void,
              completed: () => void,
              minimumVelocity: number = 5,
              amplitude: number       = 0.25,
              timeConstant: number    = 342) {

    this.getPosition  = getPosition;
    this.setPosition  = setPosition;
    this.completed    = completed;
    this.minVelocity  = minimumVelocity;
    this.amplitude    = amplitude;
    this.timeConstant = timeConstant;

    //Logger.debug(`[Kinetic] constructor -  ready!`);

  }

  /** cancel any ongoing momentum scroll */
  public cancel(persistent: boolean = false): void {

    //Logger.debug(`[Kinetic] cancel - `);

    cancelAnimationFrame(this.velocityID);
    cancelAnimationFrame(this.autoScrollID);

    this.velocityID   = 0;
    this.autoScrollID = 0;
    this.canceled     = persistent;
  }

  /**
   * the polling function we use to track the mouse dragging velocity while the
   * user is manually dragging the mouse.
   *
   * Note: time units are milliseconds!
   *
   */
  private track(): void {

    let k: Kinetic      = this;

    let now: number     = performance.now();
    let elapsed: number = now - k.timestamp;
    k.timestamp         = now;

    let currentPosition: {x: number, y: number} = k.getPosition();

    /* what is the distance delta? */

    let dx: number = currentPosition.x - k.lastPosition.x;
    let dy: number = currentPosition.y - k.lastPosition.y;

    k.lastPosition = currentPosition;

    /* what is the time delta? */

    let dt: number = 1000 / (1 + elapsed);

    /* track current velocity as a moving average */

    k.vx = 0.8 * dx * dt + 0.2 * k.vx;
    k.vy = 0.8 * dy * dt + 0.2 * k.vy;

    //Logger.debug(`[Kinetic] track - velocity vx=${k.vx}, vy=${k.vy}`);

    k.velocityID = requestAnimationFrame(() => {k.track()});
  }

  /**
   * start tracking the dragging velocity; this is how we know how
   * much momentum to give the auto-scrolling, when they release the
   * mouse button.
   *
   */
  public start() {

    //Logger.debug(`[Kinetic] start - `);

    let k: Kinetic = this;

    k.lastPosition = k.getPosition();

    k.ax = 0;
    k.ay = 0;
    k.vx = 0;
    k.vy = 0;
    k.tx = 0;
    k.ty = 0;

    k.timestamp = performance.now();

    k.cancel();

    /*
     * when they press the mouse button, we start tracking velocity so that
     * when the release it, we can calculate an acceleration to launch
     * the momentum base auto-scrolling with.
     *
     */

    k.velocityID = requestAnimationFrame(() => {k.track()});
  }

  /**
   * stop tracking the dragging velocity; they have release the mouse
   * button, and now it's time to start the momentum decay based auto-scrolling.
   *
   */
  public stop() {

    let k: Kinetic = this;

    //Logger.debug(`[Kinetic] stop - ready to launch auto-scrolling minV=${k.minVelocity}, kx=${k.vx}, ky=${k.vy}...`);

    /* we can stop everything, we are about to launch momentum based auto-scrolling */

    k.cancel();

    let currentPoint: {x: number, y: number} = k.getPosition();

    k.tx = currentPoint.x;
    k.ty = currentPoint.y;

    k.timestamp = performance.now();

    /*
     * if they were dragging slowly, just stay linear.  But above the
     * minimum velocity, given them some acceleration.
     *
     */

    if (k.vx < -k.minVelocity || k.vx > k.minVelocity) {

      k.ax = k.amplitude * k.vx;
      k.tx += k.ax;
    }

    if (k.vy < -k.minVelocity || k.vy > k.minVelocity) {

      k.ay = k.amplitude * k.vy;
      k.ty += k.ay;
    }

    /* start the momentum scrolling... */

    k.autoScrollID = requestAnimationFrame(() => {k.autoScroll()});
  }

  /** test if we are current auto-scrolling */
  public isScrolling(): boolean {

    return this.autoScrollID != 0;
  }

  /** test if we were just canceled */
  public isCanceled(): boolean {

    return this.canceled;
  }

  /**
   * start auto-scrolling; this is the momentum based decay after
   * they release the mouse button.
   *
   */
  public autoScroll() {

    let k: Kinetic      = this;

    if (k.canceled) {

      /* auto-scrolling was canceled */

      //Logger.debug(`[Kinetic] autoScroll - stopping (canceled).`);

      /* tell the application we are done auto-scrolling */

      k.completed();

      return ;
    }

    let elapsed: number = performance.now() - k.timestamp;

    /* generate the next step on the exponential decay curve */

    let moving: boolean = false;
    let dx: number      = 0;
    let dy: number      = 0;

    if (k.ax) {

      dx = -k.ax * Math.exp(-elapsed / k.timeConstant);

      //Logger.debug(`[Kinetic] autoScroll - dx=${dy}, ay=${k.ax}, elapsed=${elapsed}, tc=${k.timeConstant}`);

      if (dx > 0.5 || dx < -0.5) {

        moving = true;

      } else {

        dx = k.ax = 0;
      }
    }

    if (k.ay) {

      dy = -k.ay * Math.exp(-elapsed / k.timeConstant);

      //Logger.debug(`[Kinetic] autoScroll - dy=${dy}, ay=${k.ay}, elapsed=${elapsed}, tc=${k.timeConstant}`);

      if (dy > 0.5 || dy < -0.5) {

        moving = true;

      } else {

        dy = k.ay = 0;
      }
    }

    /* are still panning? */

    if (moving) {

      /* tell the client application to pan! */

      //Logger.debug(`[Kinetic] autoScroll - pan to: x=${k.tx + dx}, y=${k.ty + dy}.`);

      k.setPosition(k.tx + dx, k.ty + dy);

      /* continue scrolling ... */

      k.autoScrollID = requestAnimationFrame(() => {this.autoScroll();});

    } else {

      //Logger.debug(`[Kinetic] autoScroll - stopping, no movement generated.`);

      /* tell the application we are done auto-scrolling */

      k.completed();

      k.cancel();
    }

  }

}
